
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { BACKEND_URL } from "../../../constants";

import {
  Link,
  useLocation,
} from "react-router-dom";

import { useArticles } from "../../../context/ArticlesContext";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import {
  AdvertisingProvider,
} from "react-advertising";

import cogoToast from "cogo-toast";

import "react-slideshow-image/dist/styles.css";
import "reactjs-popup/dist/index.css";
import "react-h5-audio-player/lib/styles.css";

// Komponenty widoczne od razu
import WidgetCategoriesSecond from
  "components/WidgetCategoriesSecond/WidgetCategoriesSecond";

import WidgetHeaderCategories from
  "components/WidgetHeaderCategories/WidgetHeaderCategories";

import HeaderFilter from
  "components/Sections/HeaderFilter";

import Heading3 from
  "components/Heading/Heading3";

// =====================================================
// LAZY LOADING
// =====================================================

// Cięższe sekcje pobieramy dopiero wtedy,
// gdy są potrzebne.

const SectionMagazine4 = lazy(() =>
  import("components/Sections/SectionMagazine4")
);

const SectionGridPosts = lazy(() =>
  import("components/Sections/SectionGridPosts")
);

const SectionMagazineFirstSpecial = lazy(() =>
  import("components/Sections/SectionMagazineFirstSpecial")
);

const SectionMagazineWithoutHeader = lazy(() =>
  import("components/Sections/SectionMagazineWithoutHeader")
);

const SectionSliderBandwidth = lazy(() =>
  import("components/Sections/SectionSliderBandwidth")
);

const SectionSliderPodcast = lazy(() =>
  import("components/Sections/SectionSliderPodcast")
);

const SectionSliderCardText = lazy(() =>
  import("components/Sections/SectionSliderCardText")
);

const SectionMagazineSecond4 = lazy(() =>
  import("components/Sections/SectionMagazineSecond4")
);

const SectionSliderAudio = lazy(() =>
  import("components/Sections/SectionSliderAudio")
);

const SectionNewspapperOne = lazy(() =>
  import("components/Sections/SectionNewspapperOne")
);

const CardLargeVideo = lazy(() =>
  import("components/CardLargeVideo/CardLargeVideo")
);

const WidgetVideo = lazy(() =>
  import("components/WidgetVideo/WidgetVideo")
);

const FacebookFeed = lazy(() =>
  import("../SectionFacebook")
);

const InstagramPost = lazy(() =>
  import("../SectionInstagram")
);

const PageNewsletter = lazy(() =>
  import("app/(others)/newsletter/page")
);

const PageNewsletterPopup = lazy(() =>
  import("app/(others)/newsletter_popup/page")
);

// =====================================================
// API
// =====================================================

// Jedna funkcja do pobierania JSON.
// Obsługuje błędy HTTP oraz anulowanie requestów.

const fetchJSON = async (endpoint, signal) => {
  const response = await fetch(
    `${BACKEND_URL}/${endpoint}`,
    {
      method: "GET",
      signal,
    }
  );

  if (!response.ok) {
    throw new Error(
      `Błąd HTTP ${response.status}: ${endpoint}`
    );
  }

  return response.json();
};

// =====================================================
// OBSŁUGA SEKCJI ŁADOWANYCH PRZY SCROLLOWANIU
// =====================================================

const LazySection = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [element, setElement] = useState(null);

  const observerRef = useCallback((node) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element || isVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "300px 0px",
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [element, isVisible]);

  return (
    <div ref={observerRef}>
      {isVisible ? (
        <Suspense
          fallback={
            <div
              style={{
                minHeight: "200px",
              }}
            />
          }
        >
          {children}
        </Suspense>
      ) : (
        <div
          style={{
            minHeight: "200px",
          }}
        />
      )}
    </div>
  );
};

// =====================================================
// MAIN COMPONENT
// =====================================================

const PageHomeDemo6 = () => {
  const { data: responseData, loading } = useArticles();
  const data = responseData ?? {};

  // Dane artykułów pochodzą z jednego endpointu /articles.
  // Zabezpieczenie na wypadek, gdy API zwróci brak którejś tablicy.
  const magazineWithoutHeader = data.magazineWithoutHeader ?? [];
  const magazineFirstSpecial = data.magazineFirstSpecial ?? [];
  const magazine4 = data.magazine4 ?? [];
  const widgetCategoriesSecond = data.widgetCategoriesSecond ?? [];
  const widgetHeaderCategories = data.widgetHeaderCategories ?? [];
  const gridPosts = data.gridPosts ?? [];
  const textPosts = data.textPosts ?? [];
  const politicsPosts = data.politicsPosts ?? [];
  const podcastsFromApi = data.podcasts ?? [];
  const newspaperFromApi = data.newspaper ?? [];

  const location = useLocation();

  // =====================================================
  // STATES
  // =====================================================

  const [currentIndex, setCurrentIndex] = useState(0);

  const [live, setLive] = useState("");

  const [sponsored, setSponsored] = useState([]);
  const [specialmovie, setSpecialMovie] = useState([]);

  const [youtube, setYoutube] = useState([]);
  const [podcasts, setPodcasts] = useState([]);

  const [adverts, setAdverts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [newspappers, setNewspappers] = useState([]);
  const [categories, setCategories] = useState([]);

  const [categoryid, setCategoryId] = useState(0);
  const [subcategoryid, setSubCategoryId] = useState(0);

  const [isOpen, setIsOpen] = useState(true);

  const [popupShown, setPopupShown] = useState(() => {
    try {
      return (
        localStorage.getItem("isPopUpLoaded") !== "false"
      );
    } catch {
      return false;
    }
  });

  // =====================================================
  // MEMOIZED DATA
  // =====================================================

  const newFiltered = useMemo(() => magazineWithoutHeader, [magazineWithoutHeader]);

  // Pięć najnowszych artykułów obrazkowych z całej bazy.
  // Backend powinien zwracać je w data.textPosts.
  const posts = useMemo(() => textPosts, [textPosts]);

  const today = useMemo(() => {
    const todayDate = new Date().toDateString();
    return textPosts.filter((post) =>
      post.type === "image" &&
      new Date(post.date).toDateString() === todayDate
    );
  }, [textPosts]);

  const latestSection5Posts = useMemo(
    () => widgetCategoriesSecond,
    [widgetCategoriesSecond]
  );

  const visibleMovies = useMemo(() => {
    return gridPosts.filter((post) => post.status === 1);
  }, [gridPosts]);

  const visibleNewspappers = useMemo(() => {
    return newspaperFromApi.filter((news) => news.isVisible === 1).slice(0, 1);
  }, [newspaperFromApi]);

  const currentAd = banners[currentIndex];

  // =====================================================
  // LOAD DATA
  // =====================================================

  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      const requests = [
        ["categories", "categories"],
        ["lives", "lives"],
        ["adverts", "adverts"],
        ["banners", "get-banners"],
        ["sponsored", "article_sponsored"],
        ["specialmovie", "special_movie"],
        ["youtube", "youtube_videos"],
      ];

      const results = await Promise.allSettled(
        requests.map(([, endpoint]) =>
          fetchJSON(endpoint, controller.signal)
        )
      );

      if (controller.signal.aborted) return;

      const setters = {
        categories: setCategories,
        lives: setLive,
        adverts: setAdverts,
        banners: setBanners,
        sponsored: setSponsored,
        specialmovie: setSpecialMovie,
        youtube: setYoutube,
      };

      results.forEach((result, index) => {
        const [key] = requests[index];

        if (result.status !== "fulfilled") {
          console.error(
            `Nie udało się pobrać ${key}:`,
            result.reason
          );

          return;
        }

        const responseData = result.value;

        // Zachowanie struktury danych z dotychczasowego API.
        const normalizedData = {
          lives: responseData?.result,
          adverts: responseData?.result,
          youtube: responseData?.result,
        };

        const value = Object.prototype.hasOwnProperty.call(
          normalizedData,
          key
        )
          ? normalizedData[key]
          : responseData;

        setters[key](value ?? []);
      });
    };

    loadData().catch((error) => {
      if (error.name !== "AbortError") {
        console.error(
          "Błąd ładowania danych:",
          error
        );

        cogoToast.error(
          "Nie udało się załadować danych."
        );
      }
    });

    return () => {
      controller.abort();
    };
  }, []);

  // =====================================================
  // BANNER SLIDER
  // =====================================================

  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        return (prevIndex + 1) % banners.length;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [banners.length]);

  // =====================================================
  // POPUP
  // =====================================================

  const closePopup = useCallback(() => {
    setIsOpen(false);
    setPopupShown(false);

    try {
      localStorage.setItem(
        "isPopUpLoaded",
        JSON.stringify(false)
      );
    } catch (error) {
      console.error(
        "Nie udało się zapisać stanu popupu:",
        error
      );
    }
  }, []);

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="nc-PageHomeDemo6">

      {/* POPUP */}

      {popupShown && (
        <dialog
          className="popup-options-2 dark:bg-neutral-900/95 dark:text-white dark:shadow-dark"
          open
          style={{
            display: isOpen ? "flex" : "none",
          }}
        >
          <button
            type="button"
            className="button-close"
            onClick={closePopup}
            aria-label="Zamknij popup"
          >
            <img
              src="https://respublica.publit.io/file/close.png"
              alt=""
              width="24"
              height="24"
            />
          </button>

          <Suspense fallback={null}>
            <PageNewsletterPopup
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />
          </Suspense>
        </dialog>
      )}

      {/* KATEGORIE */}

      <div className="mobile-hidden">
        <WidgetCategoriesSecond
          posts={latestSection5Posts}
          loading={loading}
          categories={categories}
        />
      </div>

      {/* BANNERY */}

      <div
        style={{
          display: "flex",
          flexFlow: "column",
        }}
        className="container mobile-header"
      >
        <div className="mobile-banners">
          <AdvertisingProvider>
            <AnimatePresence mode="wait">
              {currentAd && (
                <motion.div
                  key={currentAd.image}
                  initial={{
                    x: 100,
                    opacity: 0,
                  }}
                  animate={{
                    x: 0,
                    opacity: 1,
                  }}
                  exit={{
                    x: -100,
                    opacity: 0,
                  }}
                  transition={{
                    duration: 0.2,
                    ease: "easeInOut",
                  }}
                >
                  <Link to={currentAd.link}>
                    <div
                      className="image"
                      style={{
                        backgroundImage: `url(${currentAd.image.replace(
                          ".jpg",
                          ".webp"
                        )})`,
                        backgroundSize:
                          currentAd.id === 3
                            ? "cover"
                            : "contain",
                        backgroundPosition: "center",
                        marginTop: "30px",
                        width: "100%",
                      }}
                    />
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </AdvertisingProvider>
        </div>

        <HeaderFilter heading="Aktualności" />
      </div>

      {/* ZOBACZ TEŻ */}

      <div className="container">
        <LazySection>
          <SectionSliderBandwidth
            className="py-16"
            postCardName="card10"
            width={1600}
            heading="Zobacz też"
            subHeading="Inne Artykuły"
            posts={newFiltered
              .slice(0, 5)
              .reverse()}
          />
        </LazySection>
      </div>

      {/* WYDARZENIA DNIA */}

      <div
        style={{
          paddingBottom: "0px",
          minHeight: "600px",
        }}
        className="box-main grid-container hr container"
      >
        <div
          style={{
            paddingBottom: "30px",
          }}
          className="box-width-section"
        >
          <LazySection>
            <SectionMagazineWithoutHeader
              subcategory_id={subcategoryid}
              category_id={categoryid}
              heading="Wydarzenia dnia"
              category="Gospodarka"
              posts={magazineWithoutHeader}
            />
          </LazySection>

          <LazySection>
            <CardLargeVideo
              youtube={youtube}
              size="medium"
            />
          </LazySection>
        </div>

        <div className="half-box">
          <WidgetHeaderCategories
            live={live}
            youtube={youtube}
            posts={widgetHeaderCategories}
            categories={categories}
          />
        </div>
      </div>

      <hr
        className="desktop-line"
        style={{
          padding: "0.5px",
          marginTop: "0px",
          height: "0px",
          backgroundColor: "black",
        }}
      />

      {/* PODCASTY / YOUTUBE */}

      <div className="container">
        <LazySection>
          <SectionSliderPodcast
            className="py-16"
            postCardName="card10"
            heading="Zobacz też"
            subHeading="Inne Artykuły"
            posts={youtube}
          />
        </LazySection>
      </div>

      <hr
        className="desktop-line"
        style={{
          padding: "0.5px",
          marginTop: "0px",
          height: "0px",
          backgroundColor: "black",
        }}
      />

      {/* POLECANE */}

      <div className="box-main hr container">
        <div className="box-width-section">
          <div className="box-width">
            <LazySection>
              <SectionMagazineFirstSpecial
                subcategory_id={subcategoryid}
                category_id={categoryid}
                heading="Polecane"
                category="Sport"
                posts={magazineFirstSpecial}
              />
            </LazySection>
          </div>
        </div>

        <div className="half-box">
          <Link to={adverts[2]?.link}>
            <img
              alt="live"
              width="343"
              height="274"
              loading="lazy"
              decoding="async"
              src={adverts[2]?.photo?.replace(
                ".jpg",
                ".webp"
              )}
            />
          </Link>

          <LazySection>
            <WidgetVideo podcast={specialmovie} />
          </LazySection>
        </div>
      </div>

      <hr
        className="desktop-line"
        style={{
          padding: "0.5px",
          marginTop: "0px",
          height: "0px",
          backgroundColor: "black",
        }}
      />

      {/* KOLEJNA SEKCJA ARTYKUŁÓW */}

      <div className="relative overflow-hidden">
        <div className="relative">
          <div>
            <div
              className="container"
              style={{
                paddingBottom: "30px",
              }}
            >
              <LazySection>
                <SectionMagazine4
                  subcategory_id={subcategoryid}
                  category_id={categoryid}
                  heading="Wydarzenia dnia"
                  category="Gospodarka"
                  posts={magazine4}
                />
              </LazySection>
            </div>
          </div>

          <hr
            className="desktop-line"
            style={{
              padding: "0.5px",
              marginTop: "0px",
              height: "0px",
              backgroundColor: "black",
            }}
          />

          {/* SOCIAL MEDIA */}

          <div className="mobile-header container">
            <Heading3>
              Polub nasze social media
            </Heading3>
          </div>

          <div
            style={{
              paddingBottom: "20px",
            }}
            className="kitchen-box container"
          >
            <div className="half-box-kitchen">
              <LazySection>
                <FacebookFeed />
              </LazySection>
            </div>

            <div className="half-box-kitchen-second bg-white rounded-2xl shadow-md p-4">
              <LazySection>
                <InstagramPost />
              </LazySection>
            </div>

            <div className="half-box-advert">
              <Link to={adverts[0]?.link}>
                <img
                  style={{
                    height: "491px",
                    width: "440px",
                  }}
                  alt="reklama"
                  loading="lazy"
                  decoding="async"
                  src={adverts[0]?.photo?.replace(
                    ".jpg",
                    ".webp"
                  )}
                />
              </Link>
            </div>
          </div>

          {/* ARTYKUŁY TEKSTOWE */}

          <div className="container">
            <LazySection>
              <SectionSliderCardText
                className="py-16"
                postCardName="card10"
                heading="Zobacz też"
                subHeading="Inne Artykuły"
                posts={posts}
              />
            </LazySection>
          </div>

          {/* MATERIAŁY VIDEO */}

          <div
            style={{
              paddingBottom: "2rem",
              paddingTop: "1rem",
              backgroundColor: "black",
            }}
          >
            <div className="container">
              <LazySection>
                <SectionGridPosts
                  headingIsCenter
                  postCardName="card10V2"
                  heading="Materiały video"
                  subHeading="Hover on the post card and preview video 🥡"
                  posts={visibleMovies}
                  gridClass="sm:grid-cols-2 lg:grid-cols-3"
                />
              </LazySection>
            </div>
          </div>

          {/* REKLAMA */}

          <Link to={adverts[1]?.link}>
            <AdvertisingProvider>
              <div>
                <div
                  style={{
                    backgroundImage: `url(${adverts[1]?.photo?.replace(
                      ".jpg",
                      ".webp"
                    )})`,
                  }}
                  className="image"
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      paddingRight: "35px",
                      gap: "8px",
                      paddingTop: "10%",
                      alignItems: "center",
                    }}
                  />
                </div>
              </div>
            </AdvertisingProvider>
          </Link>

          <hr
            className="desktop-line"
            style={{
              padding: "0.5px",
              marginTop: "0px",
              height: "0px",
              backgroundColor: "black",
            }}
          />

          {/* POLITYKA */}

          <LazySection>
            <SectionMagazineSecond4
              className="container"
              subcategory_id={2}
              category_id={1}
              heading="Polityka na świecie"
              posts={politicsPosts}
            />
          </LazySection>

          <div
            style={{
              marginBottom: "1rem",
            }}
          />
        </div>
      </div>

      {/* AUDIO */}

      <div className="relative container">
        <LazySection>
          <SectionSliderAudio
            postCardName="card10"
            heading="Sea travel enthusiast"
            subHeading="Over 218 articles about sea travel"
            podcasts={podcastsFromApi}
          />
        </LazySection>
      </div>

      {/* NEWSLETTER */}

      <div className="relative container">
        <div
          style={{
            paddingBottom: "2rem",
          }}
        >
          <LazySection>
            <SectionNewspapperOne
              heading="W najnowszym numerze"
              posts={visibleNewspappers}
            />
          </LazySection>
        </div>

        <LazySection>
          <PageNewsletter />
        </LazySection>
      </div>
    </div>
  );
};

export default PageHomeDemo6;
